/*
  Gestão WhatsApp: o que é compartilhado entre as telas (rótulos, modos e a prévia das mensagens).
  As chamadas HTTP ficam nas telas, como no resto do app.

  Os textos padrão e o preenchimento das variáveis são os mesmos do bot (`cutilei-bot/src/texts.ts`):
  a prévia mostra o que a cliente vai ler. Mudou lá, mude aqui.
*/

export const TONES = [
  { value: 'warm', label: 'Acolhedor' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Descontraído' }
]

export const CHAT_MODES = {
  bot: { label: 'Bot', color: '#44CF6C', description: 'O bot responde' },
  human: { label: 'Equipe', color: '#FF9800', description: 'A equipe responde; o bot fica quieto' },
  ignored: { label: 'Ignorar', color: '#8A8A8A', description: 'Ninguém responde automaticamente (contato pessoal)' }
}

export const SESSION_STATUS = {
  WORKING: { label: 'Conectado', color: '#44CF6C' },
  SCAN_QR_CODE: { label: 'Esperando a leitura do QR', color: '#FF9800' },
  STARTING: { label: 'Conectando...', color: '#FF9800' },
  STOPPED: { label: 'Desconectado', color: '#FF3939' },
  FAILED: { label: 'Conexão caiu', color: '#FF3939' }
}

/* Mensagens que o salão pode reescrever, na ordem da tela, com as variáveis que aceitam. */
export const EDITABLE_MESSAGES = [
  { key: 'greeting', title: 'Cumprimento', when: 'Primeira resposta de cada conversa', variables: ['cliente', 'saudacao', 'apresentacao', 'salao', 'assistente'] },
  { key: 'help', title: 'Pergunta inicial', when: 'Quando a cliente ainda não disse o que quer', variables: [] },
  { key: 'ask_service', title: 'Pedir o serviço', when: 'Quando falta saber o serviço', variables: [] },
  { key: 'ask_date', title: 'Pedir o dia', when: 'Quando já sabe o serviço e falta o dia', variables: ['servico'] },
  { key: 'booking_handoff', title: 'Pedido anotado', when: 'Quando a cliente já disse serviço e dia', variables: ['pedido'] },
  { key: 'handoff', title: 'Chamar a equipe', when: 'Quando a cliente pede uma pessoa ou o bot não entende duas vezes', variables: ['salao'] },
  { key: 'not_understood', title: 'Não entendi', when: 'Quando o bot não entende a mensagem', variables: [] }
]

export const MESSAGE_MAX_LENGTH = 300

const DEFAULTS = {
  warm: {
    greeting: 'Oi, {cliente}! {saudacao}, tudo bem? Aqui é {apresentacao}.',
    help: 'Em que posso te ajudar?',
    ask_service: 'Qual serviço você gostaria de fazer?',
    ask_date: 'Ótimo, {servico}. Para qual dia?',
    booking_handoff: 'Anotei: {pedido}. Vou passar para a equipe confirmar o horário com você, já já te respondem por aqui.',
    handoff: 'Vou chamar alguém do salão para falar com você. Já já te respondem por aqui.',
    not_understood: 'Desculpe, não entendi. Me conta o que você gostaria de fazer?'
  },
  formal: {
    greeting: 'Olá, {cliente}. {saudacao}! Você está falando com {apresentacao}.',
    help: 'Como posso ajudar?',
    ask_service: 'Qual serviço você deseja agendar?',
    ask_date: 'Perfeito, {servico}. Para qual dia você deseja agendar?',
    booking_handoff: 'Registrei o seu pedido: {pedido}. A equipe do salão vai confirmar o horário com você em instantes.',
    handoff: 'Vou encaminhar o seu atendimento para a equipe do salão. Em instantes alguém responde por aqui.',
    not_understood: 'Desculpe, não compreendi. Poderia me dizer o que deseja?'
  },
  casual: {
    greeting: 'Oiê, {cliente}! {saudacao}! Aqui é {apresentacao}.',
    help: 'Me conta, o que você quer fazer?',
    ask_service: 'Bora! Qual serviço você quer fazer?',
    ask_date: 'Amei, {servico}! Pra qual dia?',
    booking_handoff: 'Anotadíssimo: {pedido}. Vou passar pra equipe confirmar o horário com você, já já te chamam aqui!',
    handoff: 'Vou chamar alguém da equipe pra falar com você, tá? Já já te respondem aqui.',
    not_understood: 'Ops, não entendi. Me conta de novo o que você quer fazer?'
  }
}

const EMOJI = { greeting: '😊', booking_handoff: '💜', handoff: '🙋‍♀️' }
const hasEmoji = text => /\p{Extended_Pictographic}/u.test(text)

export function defaultMessage(tone, key) {
  return (DEFAULTS[tone] ?? DEFAULTS.warm)[key] ?? ''
}

/* Troca {variavel}. Sem valor, some junto com a vírgula ou o espaço antes ("Oi, {cliente}!" vira "Oi!"). */
export function fillTemplate(template, vars) {
  return template
    .replace(/(,\s*|\s+)?\{(\w+)\}/g, (_, before, name) => {
      const value = vars[name]
      return value === undefined || value === null || value === '' ? '' : `${before ?? ''}${value}`
    })
    .replace(/\s+([.!?,])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export function dayGreeting(now = new Date()) {
  const hour = Number(new Intl.DateTimeFormat('pt-BR', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/Sao_Paulo' }).format(now))
  if (hour >= 5 && hour < 12) return 'Bom dia'
  if (hour >= 12 && hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

/* Uma mensagem como a cliente vai ler, com o estilo que está na tela (salvo ou não). */
export function previewMessage(style, key, { salon, customer = 'Joana', vars = {}, now = new Date() } = {}) {
  const tone = style.tone ?? 'warm'
  const assistant = style.assistant_name?.trim()
  const base = {
    cliente: customer,
    salao: salon,
    assistente: assistant,
    saudacao: dayGreeting(now),
    apresentacao: tone === 'formal'
      ? (assistant ? `${assistant}, do ${salon}` : salon)
      : (assistant ? `a ${assistant}, do ${salon}` : `do ${salon}`)
  }
  const template = style.messages?.[key]?.trim() || defaultMessage(tone, key)
  const text = fillTemplate(template, { ...base, ...vars })
  return style.emojis && EMOJI[key] && !hasEmoji(text) ? `${text} ${EMOJI[key]}` : text
}

/* "21 97959-4534" a partir de "21979594534". */
export function formatPhone(phone) {
  if (!phone) return ''
  const digits = String(phone).replace(/\D/g, '')
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return digits
}

/* Hora (hoje) ou data curta, a partir do timestamp em segundos do WhatsApp. */
export function formatMessageTime(timestamp, now = new Date()) {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  const sameDay = date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) === now.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
  return sameDay
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' })
}

export const chatPath = (businessId, chatId) => `/businesses/${businessId}/whatsapp/chats/${encodeURIComponent(chatId)}`

/*
  Logo depois de conectar, o celular manda o histórico das conversas para o WhatsApp da Cutilei
  (no teste, milhares de mensagens). O WhatsApp não avisa quando termina: a tela considera os
  primeiros 3 minutos depois da conexão como sincronização.
*/
export const SYNC_WINDOW_MS = 3 * 60 * 1000

export function syncEndsAt(session) {
  if (session?.status !== 'WORKING' || !session.connected_at) return null
  return new Date(session.connected_at).getTime() + SYNC_WINDOW_MS
}
